import { formatToDecimals } from '@bg-shared/utils/formatToDecimals';
import { t } from '@bg-shared/business/Translate';
import { HttpClientFactory, HttpClientRepository } from '../../infrastructure/HttpClient';
import {
    IGameInitializationResponse,
    IGameConfig,
    IFreePlayAvailable,
    IFreePlayInfo,
} from './interfaces';
import { Operation } from './enums';

interface IError extends Error {
    status: number;
}

export class RGSGameApi {
    protected client: HttpClientRepository;

    private errorIds: Record<string, string> = {
        11: 'range_bet_exceed_limits',
        1008: 'token_expired',
    };

    constructor() {
        this.client = HttpClientFactory.getRgsClient();
        this.client.setHeadersHandler((response) => {
            const errorId = response.headers.get('x-h-error-id');

            if (errorId) {
                let errorMessage: string;

                if (this.errorIds[errorId]) {
                    errorMessage = t.hasKey('errors', this.errorIds[errorId])
                        ? t.error(this.errorIds[errorId])
                        : t.string(this.errorIds[errorId]);
                } else {
                    errorMessage = 'something_wrong';
                }

                const error = new Error(errorMessage) as IError;
                error.status = response.status || 500;
                throw error;
            }
        });
    }

    public formatFreePlayAmounts(
        freePlaysAvailable: IFreePlayAvailable[],
        freePlayInfo: IFreePlayInfo,
    ): void {
        if (freePlaysAvailable) {
            freePlaysAvailable.forEach((freePlay) => {
                freePlay.stake = formatToDecimals(freePlay.stake);
            });
        }
        if (freePlayInfo) {
            freePlayInfo.won = formatToDecimals(freePlayInfo.won);
        }
    }

    public loadGame(login: string, token: string): Promise<IGameInitializationResponse> {
        return this.client
            .get<IGameInitializationResponse>(login, {
                params: {
                    token,
                },
                transformRequest: false,
                transformResponse: false,
            })
            .then((response) => {
                if (response.gameSettings.customStakes) {
                    response.gameSettings.customStakes =
                        response.gameSettings.customStakes.map(formatToDecimals);
                }
                response.gameSettings.minStake = formatToDecimals(response.gameSettings.minStake);
                response.gameSettings.maxStake = formatToDecimals(response.gameSettings.maxStake);
                response.gameSettings.maxExposure = formatToDecimals(
                    response.gameSettings.maxExposure,
                );

                this.formatFreePlayAmounts(response.freePlaysAvailable, response.freePlayInfo);

                return response;
            });
    }

    public loadConfig<T extends IGameConfig = IGameConfig>(url: string, token: string): Promise<T> {
        return this.client
            .post<T>(url, {
                data: {
                    token,
                    op: Operation.LOAD_CONFIG,
                },
                transformRequest: false,
                transformResponse: false,
            })
            .then((response) => {
                response.config.stakes = response.config.stakes.map(formatToDecimals);
                return response;
            });
    }
}
